/**
 * @fileoverview Ponte com a extensão de navegador do WAHA para assinar o desafio
 * de passkey (WebAuthn) na origem web.whatsapp.com.
 *
 * O WhatsApp passou a exigir passkey ao vincular ALGUNS números. O desafio SÓ
 * pode ser assinado por um navegador na origem web.whatsapp.com (origin binding
 * do WebAuthn) — nenhum backend/proxy resolve isso. Este helper espelha o
 * contrato da extensão oficial "WhatsApp Browser Extension" (a mesma usada pela
 * dashboard do WAHA):
 *   - Chrome/Edge/Brave: chrome.runtime.sendMessage(extId, {type, ...}) via
 *     externally_connectable (funciona de qualquer página);
 *   - Firefox: bridge via window.postMessage <-> content script da extensão.
 * A extensão é STATELESS: não conhece nosso servidor, API key ou sessão. Ela só
 * assina o desafio e devolve a assertion.
 * Mensagens: "waha-passkey-ping" (detecção) e "waha-passkey-sign" (assinatura).
 */

export const PASSKEY_EXTENSION_ID = "ghpdcgnjffaaekflfpcgkgpbafmjldcp";
export const PASSKEY_FIREFOX_URL =
  "https://addons.mozilla.org/firefox/addon/whatsapp-browser-extension/";

export const passkeyChromeStoreUrl = (extId = PASSKEY_EXTENSION_ID) =>
  `https://chromewebstore.google.com/detail/${extId}`;

export const isFirefox = () => /firefox/i.test(navigator.userAgent);
export const isChromeLike = () => !isFirefox() && !!window.chrome;

const PING_TIMEOUT_MS = 500;
const SIGN_TIMEOUT_MS = 120000; // assinatura exige gesto do usuário no WhatsApp Web

const hasChromeMessaging = () =>
  !!(
    window.chrome &&
    window.chrome.runtime &&
    window.chrome.runtime.sendMessage
  );

// Chrome: mensagem direta à extensão (externally_connectable)
const chromeSend = (extId, message, timeoutMs) =>
  new Promise((resolve, reject) => {
    if (!hasChromeMessaging()) {
      reject(new Error("no-extension"));
      return;
    }
    const timer = setTimeout(() => reject(new Error("timeout")), timeoutMs);
    try {
      window.chrome.runtime.sendMessage(extId, message, (resp) => {
        clearTimeout(timer);
        if (window.chrome.runtime.lastError) {
          reject(
            new Error(window.chrome.runtime.lastError.message || "no-extension")
          );
          return;
        }
        resolve(resp);
      });
    } catch (e) {
      clearTimeout(timer);
      reject(e);
    }
  });

// Firefox: bridge via postMessage <-> content script da extensão
const firefoxSend = (type, extra, timeoutMs) =>
  new Promise((resolve, reject) => {
    const requestId =
      (window.crypto &&
        window.crypto.randomUUID &&
        window.crypto.randomUUID()) ||
      String(Math.random());

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("timeout"));
    }, timeoutMs);

    function onMessage(ev) {
      if (ev.source !== window) return;
      const data = ev.data;
      if (
        !data ||
        data.source !== "waha-passkey-extension" ||
        data.requestId !== requestId
      ) {
        return;
      }
      cleanup();
      resolve(data);
    }

    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
    }

    window.addEventListener("message", onMessage);
    window.postMessage(
      { source: "waha-passkey-page", requestId, type, ...(extra || {}) },
      window.location.origin
    );
  });

/** Detecta se a extensão está instalada/ativa. Nunca rejeita — resolve boolean. */
export const pingPasskeyExtension = async (extId = PASSKEY_EXTENSION_ID) => {
  try {
    if (isFirefox()) {
      const resp = await firefoxSend("waha-passkey-ping", null, PING_TIMEOUT_MS);
      return !!(resp && resp.ok);
    }
    const resp = await chromeSend(
      extId,
      { type: "waha-passkey-ping" },
      PING_TIMEOUT_MS
    );
    return !!(resp && resp.ok === true);
  } catch (e) {
    return false;
  }
};

/**
 * Pede à extensão para assinar o desafio. Retorna a assertion WebAuthn no
 * formato esperado pelo WAHA:
 *   { id, rawId, type, response:{ clientDataJSON, authenticatorData, signature, userHandle? } }
 * A extensão abre uma aba em web.whatsapp.com onde o usuário confirma com o
 * autenticador (biometria/PIN). Rejeita em caso de falha/cancelamento.
 */
export const signPasskeyChallenge = async (
  challenge,
  extId = PASSKEY_EXTENSION_ID
) => {
  if (isFirefox()) {
    const resp = await firefoxSend(
      "waha-passkey-sign",
      { challenge },
      SIGN_TIMEOUT_MS
    );
    if (!resp || !resp.ok) {
      throw new Error((resp && resp.error) || "sign-failed");
    }
    return resp.assertion;
  }
  const resp = await chromeSend(
    extId,
    { type: "waha-passkey-sign", challenge },
    SIGN_TIMEOUT_MS
  );
  if (!resp || !resp.ok) {
    throw new Error((resp && resp.error) || "sign-failed");
  }
  return resp.assertion;
};

/**
 * Fallback SEM extensão: gera um script para o usuário colar no console (DevTools)
 * da aba web.whatsapp.com. Ele reconstrói as opções WebAuthn a partir do desafio,
 * chama navigator.credentials.get, e copia a assertion (JSON) para a área de
 * transferência — o usuário cola de volta no painel. Espelha o fallback da
 * dashboard do WAHA (navigator.credentials.get + PublicKeyCredential.toJSON()).
 */
export const buildDevtoolsScript = (challenge) => {
  const json = JSON.stringify(challenge);
  return [
    "(async () => {",
    "  const c = " + json + ";",
    "  const b64u = (s) => { s = String(s).replace(/-/g,'+').replace(/_/g,'/'); s += '='.repeat((4 - (s.length % 4)) % 4); const bin = atob(s); const u = new Uint8Array(bin.length); for (let i=0;i<bin.length;i++) u[i]=bin.charCodeAt(i); return u.buffer; };",
    "  const pk = {",
    "    challenge: b64u(c.challenge),",
    "    timeout: c.timeout,",
    "    rpId: c.rpId,",
    "    userVerification: c.userVerification,",
    "    allowCredentials: (c.allowCredentials || []).map((x) => ({ id: b64u(x.id), type: x.type, transports: x.transports })),",
    "    extensions: c.extensions,",
    "  };",
    "  try {",
    "    const cred = await navigator.credentials.get({ publicKey: pk });",
    "    const out = JSON.stringify(cred.toJSON());",
    "    try { await navigator.clipboard.writeText(out); console.log('%c\\u2705 Assinatura copiada! Cole no painel Sttelar.', 'color:#25D366;font-weight:bold;font-size:14px'); } catch (e) { console.log('Copie manualmente a linha abaixo:'); }",
    "    console.log(out);",
    "  } catch (e) { console.error('\\u274C Falha ao assinar passkey:', e); }",
    "})();",
  ].join("\n");
};
